import { Web3ReactHooks, useWeb3React } from "@web3-react/core";
import { useSnackbar } from "notistack";

import { SnackMessage } from "../../components";
import { supportedChainsMap } from "../../config";
import { useCosmJsProvider } from "../../contexts";

type Provider = ReturnType<Web3ReactHooks["useProvider"]> & {
  send: (method: string, params: Array<any>) => Promise<any>;
};

export const useEvmWalletActions = (provider?: Provider) => {
  const { enqueueSnackbar } = useSnackbar();
  const { connector } = useWeb3React();
  const { connector: cosmJsConnector } = useCosmJsProvider();

  const switchChain = async (chainId: string) => {
    try {
      const evmChainId = +chainId;
      await provider?.send("wallet_switchEthereumChain", [
        { chainId: `0x${evmChainId.toString(16)}` },
      ]);
    } catch (err: any) {
      // checking 4902 (Metamask addon/Coinbase wallet) and -32603 (Metamask mobile) is not enough
      // WalletConnect uses a different type of error that's why we check by `Unrecognized chain ID`
      if (err.message.includes("Unrecognized chain ID")) {
        addChain(chainId);
        return;
      }
      enqueueSnackbar(
        <SnackMessage
          message={{
            text: `There was a problem while changing network. Error: ${
              err?.message || "unknown"
            }`,
            variant: "error",
          }}
        />,
      );
    }
  };

  const addChain = async (chainId: string) => {
    try {
      const evmChainId = +chainId;
      await provider?.send("wallet_addEthereumChain", [
        {
          chainId: `0x${evmChainId.toString(16)}`,
          chainName: supportedChainsMap.evm[chainId].fullName,
          blockExplorerUrls: [
            supportedChainsMap.evm[chainId].getTransactionExplorerUrl(""),
          ],
          rpcUrls: [supportedChainsMap.evm[chainId].rpcUrl],
          nativeCurrency: {
            name: supportedChainsMap.evm[chainId].tokenSymbol,
            symbol: supportedChainsMap.evm[chainId].tokenSymbol,
            decimals: 18,
          },
        },
      ]);
    } catch (err: any) {
      enqueueSnackbar(
        <SnackMessage
          message={{
            text: `There was a problem while adding the network. Error: ${
              err?.message || "unknown"
            }`,
            variant: "error",
          }}
        />,
      );
    }
  };

  const disconnectWallets = async () => {
    if (connector?.deactivate) {
      connector.deactivate();
    } else {
      connector.resetState();
    }
    localStorage.removeItem("lastEvmConnector");

    localStorage.removeItem("lastCosmosConnector");
    await cosmJsConnector.deactivate();
  };

  return {
    switchChain,
    addChain,
    disconnectWallets,
  };
};
